import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import { formatBRL } from '../../UI';
import { toBrazilianDate } from '../../../../helpers/utils/utils';
import {
  buscarParcelasDistribuicaoPersonalizada, descartarDistribuicaoPersonalizada, salvarDistribuicaoPersonalizada,
} from '../../../../services/core/processos/processosAcordosService';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';
import ConfirmDialog from './ConfirmDialog';

const MENSAGEM_ERRO_CARREGAR = 'Não foi possível carregar as parcelas para distribuição. Tente novamente.';
const MENSAGEM_ERRO_SALVAR = 'Não foi possível salvar a distribuição personalizada. Tente novamente.';
const MENSAGEM_ERRO_DESCARTAR = 'Não foi possível descartar a personalização. Tente novamente.';
const MENSAGEM_VALOR_INVALIDO = 'O valor digitado no campo Valor para Cliente deve ser menor ou igual ao valor da parcela.';

/**
 * Dialog "Indicadores" (Distribuição Personalizada, AW2-1133 Etapa 11) —
 * porta de `DialogDistribuicaoPersonalizada.jsx` real. A tela real já é MUI
 * v5 na grade de edição (`@mui/x-data-grid`), reaproveitada quase 1:1 — só
 * os componentes MUI v4 (`Grid24`, `Typography`/`Button` de
 * `@material-ui/core`, `TextoPadraoButton`) foram trocados pelos
 * equivalentes v5 já usados no resto deste módulo, e os ícones `react-icons`
 * por `@mui/icons-material` (nenhum outro dialog desta pasta usa
 * `react-icons`).
 *
 * Gate de habilitação do ícone que abre este dialog
 * (`isDistribuicaoPersonalizadaDesabilitado`, `acordosController.js`) é só
 * UX client-side — a autorização real (permissão 1903) permanece validada
 * no backend do service, mesmo padrão já aceito em todo o módulo
 * `prototipo/`.
 *
 * Divergência deliberada em relação à produção: `salvarDistribuicaoPersonalizada`/
 * `descartarDistribuicaoPersonalizada` reais são fire-and-forget (sem
 * `await`, sem tratamento de erro, sem feedback de sucesso/falha ao
 * usuário) — confirmado por leitura direta do código-fonte real. Aqui as 2
 * chamadas são aguardadas em segundo plano (o dialog fecha imediatamente,
 * igual à produção — mesmo padrão de `DialogFecharAcordo.jsx`, Etapa 6) e um
 * `catch` mostra erro via Snackbar caso a chamada falhe — nunca deixar uma
 * escrita real sem tratamento de erro, mesma regra já documentada em
 * `src/prototipo/CLAUDE.md` ("PUT + refetch... precisa de catch com
 * feedback visível, não só finally"). Nenhuma mudança na regra de negócio em
 * si (mesmo payload, mesmo endpoint). O botão "Salvar" (`disableBotaoSalvar`)
 * usa a mesma validação client-side real — exige que TODAS as parcelas
 * tenham sido explicitamente editadas (`parcelasAlteradas.length ===
 * tableContent.length`), não uma checagem de soma contra o valor total do
 * acordo (confirmado por leitura direta: a tela real não tem essa 2ª
 * validação).
 *
 * Divergência adicional (achado em code review): `handleEditCellChange`
 * real muta `parcelasAlteradas[objIndex]` diretamente fora de `setState`
 * (anti-padrão) e, mesmo numa edição INVÁLIDA (`valorABE < 0`, revertida
 * para os valores originais), ainda assim grava uma entrada em
 * `parcelasAlteradas` — uma parcela cuja 1ª interação é uma tentativa
 * inválida já "conta" como tocada na produção. Aqui, `setParcelasAlteradas`
 * usa atualização imutável (correção deliberada do anti-padrão) e só grava
 * a entrada em edições VÁLIDAS — uma parcela cuja 1ª interação falha nunca
 * entra em `parcelasAlteradas`. Efeito prático: `disableBotaoSalvar`
 * (`parcelasAlteradas.length !== tableContent.length`) pode habilitar
 * "Salvar" um passo mais cedo na produção (parcela "tocada" por uma
 * tentativa inválida, reenviando o valor original) do que aqui (só habilita
 * após uma edição de fato válida) — divergência aceita, nunca bloqueia uma
 * edição legítima, só adia a habilitação do botão até uma tentativa válida.
 */
export default function DialogDistribuicaoPersonalizada({
  open, acordo, idProcesso, onClose, onMutado,
}) {
  const [tableContent, setTableContent] = useState([]);
  const [valorTotal, setValorTotal] = useState(0);
  const [parcelasAlteradas, setParcelasAlteradas] = useState([]);
  const [enableValorCliente, setEnableValorCliente] = useState(true);
  const [disableBotaoDescartar, setDisableBotaoDescartar] = useState(false);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [avisoSomenteLeitura, setAvisoSomenteLeitura] = useState(false);
  const [confirmacaoSalvar, setConfirmacaoSalvar] = useState(false);
  const [confirmacaoDescartar, setConfirmacaoDescartar] = useState(false);
  const [erro, setErro] = useState('');
  const [erroValidacao, setErroValidacao] = useState('');

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setTableContent([]);
    setValorTotal(0);
    setParcelasAlteradas([]);
    setEnableValorCliente(true);
    setDisableBotaoDescartar(false);
    setSomenteLeitura(false);
    setAvisoSomenteLeitura(false);
    setConfirmacaoSalvar(false);
    setConfirmacaoDescartar(false);
    setErro('');
    setErroValidacao('');
  });

  useEffect(() => {
    if (!open || !acordo?.id) {
      return undefined;
    }
    let ignore = false;
    buscarParcelasDistribuicaoPersonalizada(acordo.id).then(({ data }) => {
      if (ignore) {
        return;
      }
      const parcelas = data.parcelaDistribuicaoPersonalizadaQueryList || [];
      setTableContent(parcelas);
      setValorTotal(data.valorTotal);
      // idStatusParcelaAcordo === 0 (Quitada, ver STATUS_PARCELA_ACORDO[0] em
      // acordosController.js) numa parcela: distribuição já foi paga, tela
      // vira somente-leitura (paridade com produção).
      if (parcelas.some(parcela => parcela.idStatusParcelaAcordo === 0)) {
        setEnableValorCliente(false);
        setDisableBotaoDescartar(true);
        setSomenteLeitura(true);
        setAvisoSomenteLeitura(true);
      } else {
        setEnableValorCliente(true);
        setDisableBotaoDescartar(parcelas[0]?.valorCliente == null);
      }
    }).catch(() => {
      if (!ignore) {
        setErro(MENSAGEM_ERRO_CARREGAR);
      }
    });
    return () => { ignore = true; };
  }, [open, acordo]);

  const onValueSetter = useCallback((value, row) => {
    const valorCliente = value || 0;
    const valorABE = row.valor - valorCliente;
    return { ...row, valorABE, valorCliente };
  }, []);

  const columns = [
    {
      field: 'nroParcela', headerAlign: 'center', headerName: 'Parcela', editable: false, align: 'center', width: 70,
    },
    {
      field: 'dataVencimento', headerAlign: 'center', headerName: 'Vencimento', editable: false, width: 96, align: 'center', valueGetter: value => toBrazilianDate(value),
    },
    {
      field: 'valor', headerAlign: 'center', headerName: 'Valor', editable: false, width: 134, align: 'right', type: 'number', valueGetter: value => formatBRL(value),
    },
    {
      field: 'valorCliente',
      headerAlign: 'center',
      headerName: 'Valor para Cliente',
      editable: enableValorCliente,
      width: 134,
      align: 'right',
      type: 'number',
      valueSetter: (value, row) => onValueSetter(value, row),
      valueFormatter: value => formatBRL(value),
    },
    {
      field: 'valorABE', headerAlign: 'center', headerName: 'Valor HB', editable: false, width: 134, align: 'right', type: 'number', valueGetter: value => formatBRL(value),
    },
  ];

  const disableBotaoSalvar = parcelasAlteradas.length !== tableContent.length;
  const disableBotaoLimpar = parcelasAlteradas.reduce((total, parcela) => total + parcela.valorCliente, 0) === 0;

  const handleEditCellChange = (updatedRow, originalRow) => {
    // `updatedRow.valorCliente < 0` (achado em code review, ausente na
    // produção): sem esse guard, um valor negativo produz
    // `valorABE = row.valor - valorCliente` positivo (soma, não subtração
    // efetiva) e passaria incólume pela única validação real.
    if (updatedRow.valorABE < 0 || updatedRow.valorCliente < 0) {
      setErroValidacao(MENSAGEM_VALOR_INVALIDO);
      return originalRow;
    }
    setParcelasAlteradas((atual) => {
      const indice = atual.findIndex(item => item.nroParcela === updatedRow.nroParcela);
      const registro = { nroParcela: updatedRow.nroParcela, valorABE: updatedRow.valorABE, valorCliente: updatedRow.valorCliente };
      if (indice >= 0) {
        const proximo = [...atual];
        proximo[indice] = registro;
        return proximo;
      }
      return [...atual, registro];
    });
    return updatedRow;
  };

  const onLimpar = () => setTableContent([]);

  const onConfirmarSalvar = async () => {
    if (enviandoRef.current || !acordo) {
      return;
    }
    const sessaoAlvo = sessaoRef.current;
    const idAcordo = acordo.id;
    const parcelas = parcelasAlteradas;
    enviandoRef.current = true;
    setConfirmacaoSalvar(false);
    onClose();

    try {
      await salvarDistribuicaoPersonalizada(idAcordo, parcelas);
    } catch {
      if (sessaoRef.current === sessaoAlvo) {
        setErro(MENSAGEM_ERRO_SALVAR);
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
      }
      if (onMutado) {
        onMutado();
      }
    }
  };

  const onConfirmarDescartar = async () => {
    if (enviandoRef.current || !acordo) {
      return;
    }
    const sessaoAlvo = sessaoRef.current;
    const idAcordo = acordo.id;
    enviandoRef.current = true;
    setConfirmacaoDescartar(false);
    onClose();

    try {
      await descartarDistribuicaoPersonalizada(idAcordo);
    } catch {
      if (sessaoRef.current === sessaoAlvo) {
        setErro(MENSAGEM_ERRO_DESCARTAR);
      }
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
      }
      if (onMutado) {
        onMutado();
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Distribuição Personalizada</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Para editar os valores, clique 2 vezes no campo &quot;Valor para Cliente&quot;:
          </Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, mb: 2,
          }}
          >
            <HandshakeOutlinedIcon color="primary" />
            <Box>
              <Typography variant="caption" color="text.secondary">Valor do Acordo</Typography>
              <Typography variant="subtitle1">{formatBRL(valorTotal)}</Typography>
            </Box>
          </Box>
          <Box sx={{ height: 290, width: '100%', mb: 2 }}>
            <DataGrid
              processRowUpdate={(params, originalRow) => handleEditCellChange(params, originalRow)}
              disableColumnMenu
              disableColumnResize
              disableColumnSorting
              rowHeight={30}
              hideFooter
              rows={tableContent}
              columns={columns}
              getRowId={row => row.nroParcela}
              columnHeaderHeight={30}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setConfirmacaoDescartar(true)}
              disabled={disableBotaoDescartar || somenteLeitura}
            >
              Descartar Personalização
            </Button>
            <Button
              variant="outlined"
              startIcon={<BackspaceOutlinedIcon />}
              onClick={onLimpar}
              disabled={disableBotaoLimpar || somenteLeitura}
            >
              Limpar
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={disableBotaoSalvar || somenteLeitura}
            onClick={() => setConfirmacaoSalvar(true)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmacaoSalvar}
        title="Distribuição Personalizada"
        text="A distribuição de valores deste acordo seguirá os valores configurados, deseja continuar?"
        onConfirmar={onConfirmarSalvar}
        onFechar={() => setConfirmacaoSalvar(false)}
      />
      <ConfirmDialog
        open={confirmacaoDescartar}
        title="Descartar Personalização"
        text="Ao descartar a personalização, a distribuição de valores será baseada nos parâmetros configurados para este cliente, deseja continuar?"
        onConfirmar={onConfirmarDescartar}
        onFechar={() => setConfirmacaoDescartar(false)}
      />
      <Dialog open={avisoSomenteLeitura} onClose={() => setAvisoSomenteLeitura(false)} maxWidth="xs" fullWidth>
        <DialogContent>
          <DialogContentText>
            Após o pagamento da primeira parcela, a distribuição personalizada não pode ser alterada
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvisoSomenteLeitura(false)} variant="contained">Voltar</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erroValidacao)}
        autoHideDuration={4000}
        onClose={() => setErroValidacao('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErroValidacao('')}>
          {erroValidacao}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogDistribuicaoPersonalizada.propTypes = {
  open: PropTypes.bool,
  acordo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    nroAcordo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired,
  onMutado: PropTypes.func,
};

DialogDistribuicaoPersonalizada.defaultProps = {
  open: false,
  acordo: null,
  idProcesso: null,
  onMutado: null,
};
